import type { Role } from "./auth-types";

/**
 * Branded type for Element IDs to ensure type safety.
 * Format: dot-separated strings with 2 to 3 segments.
 * Each segment must be alphanumeric or contains hyphens.
 */
export type ElementId = string & { readonly __brand: "ElementId" };

/**
 * Validates if a string follows the ElementId hierarchical format.
 *
 * Taxonomy:
 * - Sidebar modules: `sidebar.{moduleId}` (e.g., `sidebar.system`, `sidebar.raporty`)
 * - Sidebar groups: `sidebar.{moduleId}.{groupId}` (e.g., `sidebar.system.administracja`)
 * - Sidebar items: `sidebar.{moduleId}.{itemId}` (e.g., `sidebar.system.uzytkownicy`)
 * - Dashboard: `dashboard.{cardId}` (e.g., `dashboard.users-card`)
 * - Page elements: `page.{pageId}.{elementId}` (e.g., `page.users.impersonate-btn`)
 * - Routes: `route.{path}` (e.g., `route.konfiguracja.uzytkownicy`)
 */
export function isValidElementId(s: string): s is ElementId {
	const segments = s.split(".");
	if (segments.length < 2 || segments.length > 3) {
		return false;
	}

	const segmentRegex = /^[a-zA-Z0-9-]+$/;
	return segments.every((segment) => segmentRegex.test(segment));
}

/**
 * Represents a single visibility rule for a specific role and element.
 */
export interface VisibilityRule {
	ruleId: number;
	role: Role;
	elementId: ElementId;
	visible: boolean;
	createdAt: string;
	updatedAt: string;
}

/**
 * A matrix representing the set of visible elements for each role.
 */
export type VisibilityMatrix = Map<Role, Set<ElementId>>;

/**
 * Set of elements that are always registered or have special significance.
 */
export const PINNED_ELEMENTS = new Set<ElementId>([
	"sidebar.system.visibility-editor" as ElementId,
	"page.topbar.visibility-editor" as ElementId,
]);

/**
 * Registry for all discovered or used ElementIds (for auto-registration tracking).
 */
export type ELEMENT_REGISTRY = Set<ElementId>;

export type GrantSource = "own" | "inherited" | "blockedHere" | "blockedInherited" | "none";

export interface ResolvedElement {
	elementId: string;
	effective: boolean;
	source: GrantSource;
	grantedByRole: string;
	grantedByDisplay: string | null;
	depth: number;
}

export interface ResolveResponse {
	role: string;
	elements: ResolvedElement[];
}
